import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import Hero from "../components/experience/Hero";
import Steps from "../components/experience/Steps";
import Categories from "../components/experience/Categories";
import Featured from "../components/experience/Featured";
import PricingCoins from "../components/experience/PricingCoins";
import Perks from "../components/experience/Perks";
import Safety from "../components/experience/Safety";
import FAQ from "../components/experience/FAQ";
import ReviewMarquee from "../components/experience/ReviewMarquee";
import CTACluster from "../components/experience/CTACluster";
import StickyActions from "../components/experience/StickyActions";
import experienceContent from "../content/experience.en.json";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const Experience = () => {
  const [content, setContent] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const src = params.get("src");

  useEffect(() => {
    // Load Google Fonts for Experience page
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Fredoka:wght@300;400;500;600;700&family=Poppins:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    // Simulate async load for future i18n
    setContent(experienceContent);
    setLoading(false);
    // Analytics: fire page view with QR params
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        src: src || undefined,
      });
    }
  }, [location, src]);

  if (loading || !content) {
    return (
        <div className="flex items-center justify-center min-h-[60vh] text-lg text-gray-500">Loading experience...</div>
    );
  }

  return (
    <>
      {/* SEO Head tags */}
      <head>
        <title>Immersa VR Experience in Bethesda</title>
        <meta name="description" content="A full VR experience with motion, sound, and worlds to explore" />
        <link rel="canonical" href="https://immersa.fun/experience" />
        {/* JSON-LD for SEO */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Immersa",
            "url": "https://immersa.fun",
            "logo": "/assets/Favicons/site.webmanifest"
          })
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": content.faq.map((item: any) => ({
              "@type": "Question",
              "name": item.q,
              "acceptedAnswer": { "@type": "Answer", "text": item.a }
            }))
          })
        }} />
      </head>
      <main data-section="experience-main" className="bg-gradient-to-b from-[#0a0a23] to-[#18182f] min-h-screen">
        <Hero content={content.hero} src={src} />
        <Steps steps={content.steps} />
        <Categories categories={content.categories} />
        <Featured featured={content.featured} />
        <PricingCoins pricing={content.pricing} />
        <Perks perks={content.perks} />
        <Safety safety={content.safety} />
        <FAQ faq={content.faq} />
        <ReviewMarquee reviews={content.reviews} />
        <CTACluster cta={content.cta} src={src} />
        <StickyActions src={src} />
      </main>
    </>
  );
};

export default Experience;
